import { QuotaType } from '@bpinternal/const'
import { useQueryKeys } from '../hooks'
import { cacheDuration } from '../shared'
import { queryOptions } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { Usage } from '@botpress/client'
import { clamp } from 'lodash'
import { getApiClient, getBillingClient } from '../stores'
import { getCurrentLocalDateString } from '../utils'
import { isLegacyBilling } from '../features/billing/utils/billingVersion'
import { QUOTA_TYPE_TO_BILLING_FEATURE_ID } from '../features/billing/v4/quotaMapping'

type WorkspaceUsageParams = {
  workspaceId: string
  type: QuotaType
  /**
   * ISO date string
   */
  period: string
}

type WorkspaceUsagesParams = {
  workspaceId: string
  types: QuotaType[]
  /**
   * ISO date string
   */
  period: string
}

export const workspaceUsageQueryOptions = ({ workspaceId, type, period }: WorkspaceUsageParams) => {
  const currentPeriodStart = DateTime.now().startOf('month')
  const selectedDate = DateTime.fromISO(period)
  const isPreviousPeriod = selectedDate < currentPeriodStart

  return queryOptions({
    retry: 0,
    staleTime: isPreviousPeriod ? cacheDuration.long : cacheDuration.short,
    queryKey: useQueryKeys().getWorkspaceUsage(workspaceId, type, period),
    queryFn: async () => {
      const client = getApiClient({ workspaceId })

      return client.getUsage({
        id: workspaceId,
        type,
        period,
      })
    },
    meta: {
      suppressError: true,
    },
  })
}

type UsageWithPercentage = Usage & { percentage: number }

export const workspaceUsagesQueryOptions = <T extends QuotaType>({
  workspaceId,
  types,
  period,
}: WorkspaceUsagesParams) => {
  const currentPeriodStart = DateTime.now().startOf('month')
  const selectedDate = DateTime.fromISO(period)
  const isPreviousPeriod = selectedDate < currentPeriodStart

  return queryOptions({
    retry: 0,
    staleTime: isPreviousPeriod ? cacheDuration.long : cacheDuration.short,
    queryKey: useQueryKeys().getWorkspaceUsages(workspaceId, period, types),
    queryFn: async () => {
      const client = getApiClient({ workspaceId })
      const workspace = await client.getWorkspace({ id: workspaceId })

      // Billing v4 workspaces are blocked from the internal Usage API (getMultipleUsages -> getUsages
      // returns a 403). Fetch usage from smaug's public API instead and reshape it to the legacy format.
      if (!isLegacyBilling(workspace)) {
        const { quotas } = await getBillingClient(workspaceId).getWorkspaceUsages({
          period: toSmaugPeriod(period),
        })
        return adaptSmaugUsages<T>({ workspaceId, types, period, quotas })
      }

      const response = await client.getMultipleUsages({
        ids: [workspaceId],
        types,
        period,
      })

      return response.usages.reduce(
        (acc, usage) => {
          acc[usage.type as T] = {
            ...usage,
            percentage: calculateUsagePercentage(usage),
          }
          return acc
        },
        {} as Record<T, UsageWithPercentage>
      )
    },
    meta: {
      suppressError: true,
    },
  })
}

function calculateUsagePercentage(usage: Usage) {
  return usage.quota ? clamp((usage.value / usage.quota) * 100, 0, 100) : 0
}

type SmaugWorkspaceUsages = Awaited<ReturnType<ReturnType<typeof getBillingClient>['getWorkspaceUsages']>>['quotas']

/**
 * Smaug's `getWorkspaceUsages` expects `period` as an ISO 8601 date-time (the billing month is inferred
 * from it), whereas the usage queries deal in date-only strings (e.g. `2026-05-29`). Normalize to a
 * date-time, falling back to the original value if it cannot be parsed.
 */
function toSmaugPeriod(period: string): string {
  return DateTime.fromISO(period).toISO() ?? period
}

/**
 * Reshapes smaug's `getWorkspaceUsages` response (keyed by billing feature id) into the
 * `Record<QuotaType, UsageWithPercentage>` the usage UI consumes for billing v4 workspaces.
 *
 * Quota types with no v4 billing feature resolve to an empty usage (value/quota 0).
 */
function adaptSmaugUsages<T extends QuotaType>({
  workspaceId,
  types,
  period,
  quotas,
}: {
  workspaceId: string
  types: QuotaType[]
  period: string
  quotas: SmaugWorkspaceUsages
}): Record<T, UsageWithPercentage> {
  return types.reduce(
    (acc, type) => {
      const featureId = QUOTA_TYPE_TO_BILLING_FEATURE_ID[type]
      const entry = featureId ? quotas[featureId] : undefined
      const usage: Usage = {
        id: workspaceId,
        type,
        period,
        value: entry?.usage ?? 0,
        quota: entry?.quota ?? 0,
      }
      acc[type as T] = { ...usage, percentage: calculateUsagePercentage(usage) }
      return acc
    },
    {} as Record<T, UsageWithPercentage>
  )
}

export const workspacesUsagesQueryOptions = <T extends QuotaType>({
  workspaceIds,
  types,
  period,
}: {
  workspaceIds: string[]
  types: QuotaType[]
  /**
   * ISO date string
   */
  period: string
}) => {
  const currentPeriodStart = DateTime.now().startOf('month')
  const selectedDate = DateTime.fromISO(period)
  const isPreviousPeriod = selectedDate < currentPeriodStart

  return queryOptions({
    retry: 0,
    staleTime: isPreviousPeriod ? cacheDuration.long : cacheDuration.short,
    queryKey: useQueryKeys().getAllWorkspacesUsages(period, types),
    queryFn: async () => {
      const client = getApiClient()

      // Bucket workspaces by billing version: smaug's public API serves v4 (the internal Usage API
      // returns a 403 for them), while v3 and lower keep going through getMultipleUsages.
      const workspaces = await Promise.all(
        workspaceIds.map((id) => getApiClient({ workspaceId: id }).getWorkspace({ id }))
      )
      const legacyIds = workspaces.filter(isLegacyBilling).map((w) => w.id)
      const v4Ids = workspaces.filter((w) => !isLegacyBilling(w)).map((w) => w.id)

      const result: Record<string, Record<T, UsageWithPercentage>> = {}

      const [legacyResponse] = await Promise.all([
        legacyIds.length ? client.getMultipleUsages({ ids: legacyIds, types, period }) : undefined,
        ...v4Ids.map(async (id) => {
          const { quotas } = await getBillingClient(id).getWorkspaceUsages({ period: toSmaugPeriod(period) })
          result[id] = adaptSmaugUsages<T>({ workspaceId: id, types, period, quotas })
        }),
      ])

      legacyResponse?.usages.forEach((usage) => {
        if (!result[usage.id]) {
          result[usage.id] = {} as Record<T, UsageWithPercentage>
        }

        result[usage.id]![usage.type as T] = {
          ...usage,
          percentage: calculateUsagePercentage(usage),
        }
      })

      return result
    },
    meta: {
      suppressError: true,
    },
  })
}

export const usageBreakdownUsageQueryOptions = ({
  workspaceId,
  type,
  period = getCurrentLocalDateString(),
}: WorkspaceUsageParams) => {
  const currentPeriodStart = DateTime.now().startOf('month')
  const selectedDate = DateTime.fromISO(period)
  const isPreviousPeriod = selectedDate < currentPeriodStart

  return queryOptions({
    retry: 0,
    staleTime: isPreviousPeriod ? cacheDuration.long : cacheDuration.short,
    queryKey: useQueryKeys().getWorkspaceUsageByBot(workspaceId, type, period),
    queryFn: async () => {
      return getApiClient({ workspaceId })
        .breakDownWorkspaceUsageByBot({
          id: workspaceId,
          type,
          period,
        })
        .then((response) => {
          return { type, usages: [...response.data].sort((usageA, usageB) => usageB.value - usageA.value) }
        })
    },
    meta: {
      suppressError: true,
    },
  })
}

export const highestWorkspacesUsageQuery = () => {
  const queryKeys = useQueryKeys()
  return queryOptions({
    queryKey: queryKeys.getWorkspaceQuotaCompletion(),
    queryFn: async () => {
      const client = getApiClient()
      try {
        const response = await client.getAllWorkspaceQuotaCompletion({})
        return response
      } catch (err) {
        throw new Error('An error occured while processing the usages')
      }
    },
  })
}
